// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    RadioButtons,
    Stack,
    Columns,
    Column,
    Heading
} from '@locus-taxy/neo-ui';

import ManualSlotGenerator from './ManualSlotGenerator';
import AutoSlotGenerator from './AutoSlotGenerator';
import StatusEnabler from './StatusEnabler';
import VisualSlotsList from './VisualSlotsList';
import {
    generateSlots,
    getRadioButtonOptions,
    SLOT_GENERATION_TYPE,
    type SlotShift,
    type RescheduleConfig
} from './helper';

const ComponentMapping = {
    manual: ManualSlotGenerator,
    auto: AutoSlotGenerator
};

type Props = {
    value: RescheduleConfig,
    onChange: (value: RescheduleConfig) => void,
    translationPath: string,
    label: string
};

const SlotManagementUI = ({
    value: rescheduleConfig,
    onChange,
    translationPath,
    label
}: Props): React.Node => {
    const [generatedSlots, setGeneratedSlots] = React.useState([]);
    const { t } = useTranslation('platform');

    const getString = (key: string): string => t(`${translationPath}.${key}`);

    const { allowReschedule, staticSlots } = rescheduleConfig;

    const [type, setType] = React.useState(
        staticSlots?.length
            ? SLOT_GENERATION_TYPE.manual
            : SLOT_GENERATION_TYPE.auto
    );

    const handleAllowReschedule = () =>
        handleSetConfig(`allowReschedule`, !allowReschedule);

    const handleSetConfig = (
        configPath: string,
        value: number | string | boolean | Array<SlotShift>
    ) => {
        const modifiedRescheduleConfig = {
            ...rescheduleConfig,
            [configPath]: value
        };
        onChange(modifiedRescheduleConfig);
    };

    const handleRadioChange = ({ key }) => {
        // remove all static slots if user goes to auto generation, this prevents saving both data
        if (key === SLOT_GENERATION_TYPE.auto) {
            handleSetConfig('staticSlots', []);
        }
        setType(key);
    };
    const Component = ComponentMapping[type] || null;

    const handleGenerateSlots = () => {
        setGeneratedSlots(generateSlots(rescheduleConfig));
    };

    const visualSlots =
        type === SLOT_GENERATION_TYPE.manual ? staticSlots : generatedSlots;

    return (
        <div>
            <Heading level="2" size="large" mb={4}>
                {label}
            </Heading>
            <StatusEnabler
                allowReschedule={allowReschedule}
                onChange={handleAllowReschedule}
                getString={getString}
            />
            {allowReschedule ? (
                <Box>
                    <Heading level="2" size="large" mb={4}>
                        {getString('createSlots.label')}
                    </Heading>
                    <Stack>
                        <Box
                            mb={4}
                            backgroundColor="white"
                            borderRadius="4px"
                            padding={4}
                            px={6}
                        >
                            <RadioButtons
                                value={type}
                                size="medium"
                                label=""
                                options={getRadioButtonOptions(getString)}
                                onChange={handleRadioChange}
                                row
                            />
                        </Box>
                        {Component ? (
                            <Columns space="medium">
                                <Column width="3/4">
                                    <Component
                                        rescheduleConfig={rescheduleConfig}
                                        setConfig={handleSetConfig}
                                        generateSlots={handleGenerateSlots}
                                        getString={getString}
                                    />
                                </Column>
                                <Column>
                                    <VisualSlotsList slots={visualSlots} />
                                </Column>
                            </Columns>
                        ) : null}
                    </Stack>
                </Box>
            ) : null}
        </div>
    );
};

export default SlotManagementUI;
